import React, { useState, useRef, useEffect } from "react";

import BcSepIcon from "../../asset/images/about/breadcrumb-separator.svg";

// Icons for advantages
import PowerIcon    from "../../asset/images/products/precicon-r/icon-power.svg";
import CheckIcon    from "../../asset/images/products/precicon-r/icon-check.svg";
import ShieldIcon   from "../../asset/images/products/precicon-r/icon-shield.svg";
import HardhatIcon  from "../../asset/images/products/precicon-r/icon-hardhat.svg";
import RapperIcon   from "../../asset/images/products/precicon-r/icon-rapper.svg";
import FactoryIcon  from "../../asset/images/products/precicon-r/icon-factory.svg";
import LeafIcon     from "../../asset/images/products/precicon-r/icon-leaf.svg";

// HMI image for spec table
import HmiImg from "../../asset/images/products/precicon-r/hmi.png";

// Product image
import ProductImg from "../../asset/images/products/precicon-r/product.webp";


const ADVANTAGES = [
  { icon: PowerIcon,   text: "Thyristor-based ESP power controller for stable and efficient operation." },
  { icon: CheckIcon,   text: "Supports 1-phase input with precise output for effective dust separation." },
  { icon: ShieldIcon,  text: "IP-54-rated HMI protection and CAN Bus compatibility for seamless integration." },
  { icon: HardhatIcon, text: "Advanced galvanic signal isolation ensures safety and reliability." },
  { icon: RapperIcon,  text: "In-built rapping controls for improved dust collection efficiency." },
];

const END_APPLICATIONS = [
  { icon: FactoryIcon, text: "Industries: Cement, steel, power, paper & pulp, and chemical." },
  { icon: LeafIcon,    text: "Ideal for achieving environmental compliance and minimizing downtime." },
];

const SPECS = [
  { label: "Controller",                    value: "Precicon R",       isImage: false },
  { label: "HMI",                           value: HmiImg,             isImage: true  },
  { label: "No Of Controller Operated By 1 HMI", value: "30 Nos",     isImage: false },
  { label: "HMI IP Protection",             value: "IP 54",            isImage: false },
  { label: "Operating Technology",          value: "Thyristor Based",  isImage: false },
  { label: "Input Phase",                   value: "1 Phase (L-L)",    isImage: false },
  { label: "Output Phase",                  value: "1 Phase (L-L)",    isImage: false },
  { label: "Line Frequency",                value: "50/60 Hz",         isImage: false },
  { label: "Input Voltage",                 value: "380-690V (L-L)",   isImage: false },
  { label: "Output Voltage",                value: "As Per Rated TR",  isImage: false },
  { label: "Controller Input Supply",       value: "110 VAC, -10% +10%, 50/60 Hz", isImage: false },
  { label: "No. Input Signal",              value: "10",               isImage: false },
  { label: "No. Output Signal",             value: "4",                isImage: false },
  { label: "Output Signal Protection",      value: "Galvanic Isolation", isImage: false },
  { label: "Output Resistance",             value: "750 Ohms (Max)",   isImage: false },
  { label: "Communication Protocol",        value: "CAN Bus",          isImage: false },
  { label: "In Built No. Of Rapper Controlling", value: "4",           isImage: false },
  { label: "Remote Monitoring",             value: "Data Can Be Monitored On DCS With The Help Of Gateway DCSIM", isImage: false },
  { label: "Ambient Temp",                  value: "50°C",             isImage: false },
  { label: "Humidity",                      value: "95% Max, Non-Condensing", isImage: false },
  { label: "Approvals",                     value: "CE, CSA",          isImage: false },
];

const FAQS = [
  {
    q: "What makes Precicon R ideal for Electrostatic Precipitators (ESPs)?",
    a: "Precicon R is a thyristor-based power controller designed to deliver stable and efficient power for ESPs. Its precise 1-phase output enhances dust separation efficiency, ensuring reliable performance in demanding industrial environments.",
  },
  {
    q: "How does Precicon R improve operational reliability?",
    a: "With features like advanced galvanic signal isolation for safety, IP-54-rated HMI protection for durability, and in-built rapping controls for dust collection efficiency, Precicon R ensures uninterrupted and reliable operation even under fluctuating conditions.",
  },
  {
    q: "What industries benefit most from Precicon R?",
    a: "Precicon R is ideal for industries such as cement, steel, power, paper & pulp, and chemicals. It helps businesses achieve environmental compliance and maintain consistent performance.",
  },
  {
    q: "How does Precicon R support system integration?",
    a: "Precicon R is compatible with CAN Bus communication, ensuring seamless integration with existing systems. Its user-friendly design simplifies operation and supports efficient system upgrades.",
  },
  {
    q: "What are the cost and environmental benefits of Precicon R?",
    a: "By enhancing dust collection efficiency, reducing downtime, and ensuring precise power delivery, Precicon R minimizes operational costs while helping industries meet strict environmental regulations.",
  },
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .pcr-wrap { font-family: 'Poppins', sans-serif; color: #202020; background: #fff; }

  /* ══════════════════════════════════════════
     1. BREADCRUMB
  ══════════════════════════════════════════ */
  .pcr-breadcrumb { padding: 60px 0 0 0; }
  .pcr-breadcrumb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .pcr-bc-item {
    font-size: 16px;
    font-weight: 500;
    color: #202020;
    text-decoration: none;
    transition: color 0.15s;
  }
  .pcr-bc-item:hover { color: #EB2019; }
  .pcr-bc-item.pcr-active { color: #202020; }
  .pcr-bc-sep {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin: 0 8px;
    flex-shrink: 0;
  }

  /* ══════════════════════════════════════════
     SHARED INNER
  ══════════════════════════════════════════ */
  .pcr-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }

  /* ══════════════════════════════════════════
     2. INTRO — image left + title + paras right
  ══════════════════════════════════════════ */
  .pcr-intro {
    padding: 60px 0 120px 0;
  }
  .pcr-intro-grid {
    display: flex;
    gap: 75px;
    align-items: center;
  }
  .pcr-intro-img-wrap {
    flex: 0 0 400px;
    border-radius: 10px;
    overflow: hidden;
  }
  .pcr-intro-img {
    width: 100%;
    height: 550px;
    object-fit: cover;
    display: block;
  }
  .pcr-intro-right {
    flex: 1;
  }
  .pcr-intro-title {
    font-size: 32px;
    font-weight: 600;
    color: #202020;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .pcr-para {
    font-size: 16px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }
  .pcr-para + .pcr-para { margin-top: 18px; }

  /* ══════════════════════════════════════════
     SHARED DIVIDER
  ══════════════════════════════════════════ */
  .pcr-divider {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }
  .pcr-divider hr {
    border: none;
    border-top: 1px solid #D4D4D4;
  }

  /* ══════════════════════════════════════════
     3. ADVANTAGES & APPLICATIONS
  ══════════════════════════════════════════ */
  .pcr-adv-section {
    padding: 0px 0 60px 0;
  }
  .pcr-section-red-title {
    font-size: 32px;
    font-weight: 600;
    color: #EB2019;
    text-align: center;
    margin-bottom: 0px;
  }
    .pcr-section-red-title1 {
     font-size: 32px;
    font-weight: 600;
    color: #EB2019;
    text-align: center;
    
    margin-bottom: 60px;
  }
  .pcr-sub-heading {
    font-size: 32px;
    font-weight: 600;
    color: #202020;
    text-align: center;
    margin-bottom: 34px;
    padding-top:60px;
  }

  /* Icon cards grid */
  .pcr-icon-row {
    display: flex;
    justify-content: center;
    gap: 31px;
    margin-bottom: 60px;
  }
  .pcr-icon-row:last-child {
    display: flex;
    justify-content: center;
    gap: 31px;
    margin-bottom: 0px;
  }
  .pcr-icon-card {
    flex: 0 0 445px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    text-align: center;
  }
  .pcr-icon-circle {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .pcr-icon-circle svg {
    width: 84px;
    height: 84px;
    filter: brightness(0) invert(1);
    display: block;
  }
  .pcr-icon-text {
    font-size: 20px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }

  /* ══════════════════════════════════════════
     4. END APPLICATIONS
  ══════════════════════════════════════════ */
  .pcr-app-section {
    padding: 0 0 80px 0;
  }
  .pcr-contact-para {
    font-size: 20px;
    font-weight: 500;
    color: #202020;
    text-align: center;
    line-height: 160%;
  }
  .pcr-contact-para a {
    color: #EB2019;
    text-decoration: none;
  }
  .pcr-contact-para a:hover { text-decoration: underline; }

  /* ══════════════════════════════════════════
     5. PRODUCT SPECIFICATIONS TABLE
  ══════════════════════════════════════════ */
  .pcr-spec-section {
    padding: 80px 0 0px 0;
  }
  .pcr-spec-table-wrap {
    border: 1px solid #D9D9D9;
    border-radius: 0;
    overflow: hidden;
    max-width: 1158px;
    margin: 0 auto;
    
  }
  .pcr-spec-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 16px;
    color: #202020;
    
  }
  .pcr-spec-table thead tr { background: #F6F6F6; }
  .pcr-spec-table thead th {
    padding: 30px 24px;
    font-weight: 600;
    text-align: center;
    line-height:120%;
    border-bottom: 1px solid #B8B8B8;
    border-right: 1px solid #B8B8B8;
  }
  .pcr-spec-table thead th:last-child { border-right: none; }
  .pcr-spec-table tbody tr { border-bottom: 1px solid #D9D9D9; }
  .pcr-spec-table tbody tr:last-child { border-bottom: none; }
  .pcr-spec-table tbody tr:nth-child(even) { background: #F6F6F6; }
  .pcr-spec-table tbody td {
    padding: 30px 24px;
    text-align: center;
    border-right: 1px solid #B8B8B8;
    vertical-align: middle;
    font-weight: 400;
    line-height:120%;
  }
  .pcr-spec-table tbody td:last-child { border-right: none; }
  .pcr-spec-hmi-img {
    width: 90px;
    height: 60px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }

  /* ══════════════════════════════════════════
     6. FAQ SECTION
  ══════════════════════════════════════════ */
  .pcr-faq-section {
    padding: 120px 0 120px 0;
  }
  .pcr-faq-grid {
    display: flex;
    gap: 154px;
    align-items: flex-start;
  }
  .pcr-faq-title {
    flex: 0 0 391px;
    font-size: 42px;
    font-weight: 600;
    color: #202020;
    line-height: 140%;
  }
  .pcr-faq-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* FAQ item wrapper */
  .pcr-faq-item {
    border-radius: 5px;
    overflow: hidden;
  }
  .pcr-faq-item-closed {
    background: #F6F6F6;
  }
  .pcr-faq-item-open {
    background: #F6F6F6;
    
  }

  /* Question button */
  .pcr-faq-question {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 34px 20px 30px;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
  }
  .pcr-faq-q-text-open {
    font-size: 16px;
    font-weight: 500;
    color: #EB2019;
    line-height: 120%;
    flex: 1;
  }
  .pcr-faq-q-text-closed {
    font-size: 16px;
    font-weight: 400;
    color: #202020;
    line-height: 150%;
    flex: 1;
  }
  .pcr-faq-chevron {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ── Smooth accordion answer panel ── */
  .pcr-faq-body {
    overflow: hidden;
    transition: max-height 0.4s ease, opacity 0.4s ease;
    opacity: 0;
    max-height: 0;
  }
  .pcr-faq-body.pcr-faq-body-open {
    opacity: 1;
  }
  .pcr-faq-answer {
    padding: 0 48px 24px 30px;
    font-size: 14px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }

  /* ══════════════════════════════════════════
     RESPONSIVE — TABLET 768px
  ══════════════════════════════════════════ */
  @media (max-width: 768px) {
    .pcr-breadcrumb        { padding: 28px 0 0 0; }
    .pcr-breadcrumb-inner  { padding: 0 32px; }
    .pcr-inner             { padding: 0 32px; }
    .pcr-divider           { padding: 0 32px; }

    .pcr-intro             { padding: 40px 0 40px 0; }
    .pcr-intro-grid        { flex-direction: column; gap: 24px; }
    .pcr-icon-circle img { width:60px; height:60px;}
    .pcr-icon-circle { width:54px;height:54px;}
    .pcr-icon-text {     font-size: 16px;}
    .pcr-intro-img-wrap    { flex: none; width: 100%; }
    .pcr-intro-img         { height: 280px; }
    .pcr-intro-title       { font-size: 22px; }
    .pcr-para              { font-size: 14px; }

    .pcr-adv-section       { padding: 0px 0 40px 0; }
    .pcr-section-red-title { font-size: 22px; margin-bottom: 0px; }
    .pcr-sub-heading       { font-size: 20px; margin-bottom: 30px; padding-top:30px;}
    .pcr-icon-row          { flex-wrap: wrap; gap: 24px; margin-bottom: 30px; }
    .pcr-app-section { padding: 0 0 40px 0;}
    .pcr-icon-card         { flex: 0 0 210px; }
    .pcr-contact-para {  font-size: 16px; text-align: left;}
      .pcr-section-red-title1 { font-size: 22px; margin-bottom: 24px; }

    .pcr-spec-section      { padding: 40px 0 40px 0; }
    .pcr-spec-table        { font-size: 14px; }
    .pcr-spec-table thead th { padding: 14px 12px; }
    .pcr-spec-table tbody td { padding: 14px 12px; }

    .pcr-faq-section       { padding: 0px 0 60px 0; }
    .pcr-faq-grid          { flex-direction: column; gap: 24px; }
    .pcr-faq-title         { flex: none; font-size: 22px; margin:0 auto;}
    .pcr-faq-q-text-open   { font-size: 14px; }
    .pcr-faq-list {     gap: 16px;}
    .pcr-faq-question {     padding: 20px 34px 16px 30px;}
    .pcr-faq-answer {     padding: 0 44px 24px 30px;}
    .pcr-faq-q-text-closed { font-size: 14px; }
    .pcr-faq-answer        { font-size: 14px; }
  }

  /* ══════════════════════════════════════════
     RESPONSIVE — MOBILE 480px
  ══════════════════════════════════════════ */
  @media (max-width: 480px) {
    .pcr-breadcrumb        { padding: 30px 0 0 0; }
    .pcr-breadcrumb-inner  { padding: 0 16px; gap: 14px; }
    .pcr-bc-item           { font-size: 14px; }
    .pcr-bc-sep            { width: 18px; height: 18px; margin: 0 4px; }
    .pcr-inner             { padding: 0 16px; }
    .pcr-divider           { padding: 0 16px; }

    .pcr-intro             { padding: 30px 0 0px 0; }
        .pcr-intro-grid { gap:30px;}
    .pcr-intro-img         { height: 432px; }
    .pcr-intro-title       { font-size: 20px; margin-bottom: 20px; }

    .pcr-adv-section       { padding: 60px 0 60px 0; }
    .pcr-section-red-title { font-size: 20px; margin-bottom: 0px; }
    .pcr-sub-heading       { font-size: 20px; margin-bottom: 30px; }
    .pcr-icon-row          { flex-direction: column; align-items: start; gap: 30px; margin-bottom: 30px;  }
    .pcr-icon-card         { flex: none; width: 100%; max-width: 100%; text-align: left;align-items: flex-start;}
        .pcr-icon-circle img { width: 60px; height:60px;}
    .pcr-icon-text         { font-size: 16px; }
       

    .pcr-app-section       { padding: 0 0 60px 0; }
    .pcr-contact-para      { font-size: 14px; margin-top: 30px; }
       .pcr-section-red-title1 { font-size: 20px;}
    .pcr-spec-section      { padding: 40px 0 0px 0; }
    .pcr-spec-table-wrap   { overflow-x: auto; }
    .pcr-spec-table        {  min-width:auto; }
    .pcr-spec-table thead th { padding: 30px 8px; font-size: 16px; }
    .pcr-spec-table tbody td { padding: 30px 8px; font-size: 14px; }

    .pcr-faq-section       { padding: 60px 0 60px 0; }
        .pcr-faq-grid { gap:30px;}
    .pcr-faq-title         { font-size: 20px; }
    .pcr-faq-question      { padding: 24px 16px 20px 16px; }
    .pcr-faq-answer        { padding: 0 16px 16px 16px; font-size: 13px; }
  }
`;

/* Chevron SVGs */
const ChevronUp = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M5 12.5L10 7.5L15 12.5" stroke="#202020" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M5 7.5L10 12.5L15 7.5" stroke="#202020" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ── Smooth FAQ accordion item ── */
const FaqItem = ({ q, a, isOpen, onToggle }) => {
  const bodyRef = useRef(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (isOpen) {
      /* expand: set max-height to scrollHeight so it animates to full height */
      el.style.maxHeight = el.scrollHeight + "px";
    } else {
      /* collapse: first lock current height, then animate to 0 */
      el.style.maxHeight = el.scrollHeight + "px";
      // force reflow so transition fires
      el.getBoundingClientRect();
      el.style.maxHeight = "0px";
    }
  }, [isOpen]);

  return (
    <div className={`pcr-faq-item ${isOpen ? "pcr-faq-item-open" : "pcr-faq-item-closed"}`}>
      <button className="pcr-faq-question" onClick={onToggle}>
        <span className={isOpen ? "pcr-faq-q-text-open" : "pcr-faq-q-text-closed"}>{q}</span>
        <span className="pcr-faq-chevron">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
      </button>
      <div
        ref={bodyRef}
        className={`pcr-faq-body${isOpen ? " pcr-faq-body-open" : ""}`}
      >
        <p className="pcr-faq-answer">{a}</p>
      </div>
    </div>
  );
};

export default function PreiconR() {
  /* First FAQ open by default (index 0), accordion: only one open at a time */
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (i) => {
    setOpenIndex(prev => (prev === i ? null : i));
  };

  return (
    <div className="pcr-wrap">
      <style>{CSS}</style>

      {/* ══ 1. BREADCRUMB ══ */}
      <nav className="pcr-breadcrumb" aria-label="Breadcrumb">
        <div className="pcr-breadcrumb-inner">
          <a href="/" className="pcr-bc-item">Home</a>
          <span className="pcr-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <a href="#" className="pcr-bc-item">Business</a>
          <span className="pcr-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <a href="/products" className="pcr-bc-item">Clean Air</a>
          <span className="pcr-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <span className="pcr-bc-item pcr-active">Precicon R</span>
        </div>
      </nav>

      {/* ══ 2. INTRO ══ */}
      <section className="pcr-intro">
        <div className="pcr-inner">
          <div className="pcr-intro-grid">

            {/* Left — product image */}
            <div className="pcr-intro-img-wrap">
              <img className="pcr-intro-img" src={ProductImg} alt="Precicon R" />
            </div>

            {/* Right — title + paragraphs */}
            <div className="pcr-intro-right">
              <h1 className="pcr-intro-title">
                Precicon R – Reliable Thyristor-Based Power Supply For Electrostatic Precipitators
              </h1>
              <p className="pcr-para">
                Ador Powertron's Precicon R is a thyristor-based ESP power controller designed to provide stable and efficient power for Electrostatic Precipitators (ESPs). Engineered for performance in demanding industrial environments, it supports 1-phase input and delivers precise, controlled output for improved dust separation. With IP-54-rated HMI protection and compatibility with CAN Bus communication, the system ensures seamless integration and ease of operation.
              </p>
              <p className="pcr-para">
                Precicon R offers robust performance across industries like cement, steel, power, paper &amp; pulp, and chemical, meeting environmental compliance while minimizing downtime. Built with advanced galvanic signal isolation for safe operation and in-built rapping controls for enhanced dust collection efficiency, it ensures reliable operation even under fluctuating conditions.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ══ 3. ADVANTAGES ══ */}
      <section className="pcr-adv-section">
        <div className="pcr-inner">
          <h2 className="pcr-section-red-title">Precicon R3 : Advantages &amp; Applications</h2>
          <h3 className="pcr-sub-heading">Advantages</h3>
        

          {/* Row 1 — 3 cards */}
          <div className="pcr-icon-row">
            {ADVANTAGES.slice(0, 3).map((item, i) => (
              <div key={i} className="pcr-icon-card">
                <div className="pcr-icon-circle">
                  <img src={item.icon} alt="" />
                </div>
                <p className="pcr-icon-text">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Row 2 — 2 cards centered */}
          <div className="pcr-icon-row">
            {ADVANTAGES.slice(3, 5).map((item, i) => (
              <div key={i} className="pcr-icon-card">
                <div className="pcr-icon-circle">
                  <img src={item.icon} alt="" />
                </div>
                <p className="pcr-icon-text">{item.text}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="pcr-divider"><hr /></div>

      {/* ══ 4. END APPLICATIONS ══ */}
      <section className="pcr-app-section">
        <div className="pcr-inner">
          <h3 className="pcr-sub-heading" >End Applications</h3>

          {/* 2 cards centered */}
          <div className="pcr-icon-row">
            {END_APPLICATIONS.map((item, i) => (
              <div key={i} className="pcr-icon-card">
                <div className="pcr-icon-circle">
                  <img src={item.icon} alt="" />
                </div>
                <p className="pcr-icon-text">{item.text}</p>
              </div>
            ))}
          </div>

          <p className="pcr-contact-para">
            Get in touch <a href="/contact">with us</a> today to learn how Precicon R can help your ESP systems achieve peak efficiency and regulatory compliance.
          </p>
        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="pcr-divider"><hr /></div>

      {/* ══ 5. PRODUCT SPECIFICATIONS ══ */}
      <section className="pcr-spec-section">
        <div className="pcr-inner">
          <h2 className="pcr-section-red-title1">Product Specifications</h2>
          <div className="pcr-spec-table-wrap">
            <table className="pcr-spec-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Conventional</th>
                </tr>
              </thead>
              <tbody>
                {SPECS.map((row, i) => (
                  <tr key={i}>
                    <td>{row.label}</td>
                    <td>
                      {row.isImage
                        ? <img className="pcr-spec-hmi-img" src={row.value} alt="HMI" />
                        : row.value
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ══ 6. FAQ ══ */}
      <section className="pcr-faq-section">
        <div className="pcr-inner">
          <div className="pcr-faq-grid">

            {/* Left — title */}
            <h2 className="pcr-faq-title">Frequently Asked Questions (FAQs)</h2>

            {/* Right — accordion */}
            <div className="pcr-faq-list">
              {FAQS.map((faq, i) => (
                <FaqItem
                  key={i}
                  q={faq.q}
                  a={faq.a}
                  isOpen={openIndex === i}
                  onToggle={() => handleToggle(i)}
                />
              ))}
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}